import { useCallback, useEffect, useState } from 'react';
import { domainsApi } from '@/api/domains';
import { getApiErrorMessage } from '@/api/errors';
import { useResource } from '@/hooks/useResource';
import type {
  ImportPleskCertificateResponse,
  NodeStatusItem,
  SetupCDNNodeResponse,
} from '@/types/domain';

/**
 * Progress of a per-node operation (setup / certificate import)
 */
export interface NodeOperationState<R> {
  loading: boolean;
  result?: R;
  error?: string;
}

type OperationMap<R> = Record<string, NodeOperationState<R>>;

/**
 * Per-node deployment state of a domain: status of cert/nginx/DNS on each
 * node plus the "setup node" and "import Plesk certificate" operations.
 *
 * @param domainId - domain to load statuses for; null disables loading
 */
export function useDomainDeployment(domainId: string | null) {
  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await domainsApi.getNodeStatus(domainId as string, { signal });
      return response.node_statuses ?? [];
    },
    [domainId]
  );

  const statuses = useResource<NodeStatusItem[]>(domainId ? fetcher : null, {
    errorMessage: 'Failed to load node status',
    keepPreviousData: false,
  });
  const reloadStatuses = statuses.reload;

  const [setupStates, setSetupStates] = useState<OperationMap<SetupCDNNodeResponse>>({});
  const [importStates, setImportStates] = useState<OperationMap<ImportPleskCertificateResponse>>({});

  const reset = useCallback(() => {
    setSetupStates({});
    setImportStates({});
  }, []);

  // A different domain starts with a clean slate
  useEffect(() => {
    reset();
  }, [domainId, reset]);

  const runSetup = useCallback(async (targetDomainId: string, nodeId: string) => {
    setSetupStates((prev) => ({ ...prev, [nodeId]: { loading: true } }));
    try {
      const result = await domainsApi.setupNode(targetDomainId, { node_id: nodeId });
      setSetupStates((prev) => ({ ...prev, [nodeId]: { loading: false, result } }));
    } catch (err) {
      const error = getApiErrorMessage(err, 'Setup failed');
      setSetupStates((prev) => ({ ...prev, [nodeId]: { loading: false, error } }));
    }
  }, []);

  /**
   * Install certificate, configure nginx and add the DNS record on one node
   */
  const setupNode = useCallback(
    async (nodeId: string) => {
      if (!domainId) return;
      await runSetup(domainId, nodeId);
      void reloadStatuses();
    },
    [domainId, runSetup, reloadStatuses]
  );

  /**
   * Set up several nodes one after another (e.g. nodes just added to a domain).
   * Takes the domain id explicitly because it may have just been created.
   */
  const setupNodes = useCallback(
    async (targetDomainId: string, nodeIds: string[]) => {
      for (const nodeId of nodeIds) {
        await runSetup(targetDomainId, nodeId);
      }
      if (targetDomainId === domainId) {
        void reloadStatuses();
      }
    },
    [domainId, runSetup, reloadStatuses]
  );

  /**
   * (Re-)import the domain's certificate from a Plesk node
   */
  const importCertificate = useCallback(
    async (nodeId: string) => {
      if (!domainId) return;
      setImportStates((prev) => ({ ...prev, [nodeId]: { loading: true } }));
      try {
        const result = await domainsApi.importPleskCertificate(domainId, {
          node_id: nodeId,
          force_reimport: true,
        });
        setImportStates((prev) => ({ ...prev, [nodeId]: { loading: false, result } }));
        void reloadStatuses();
      } catch (err) {
        const error = getApiErrorMessage(err, 'Import failed');
        setImportStates((prev) => ({ ...prev, [nodeId]: { loading: false, error } }));
      }
    },
    [domainId, reloadStatuses]
  );

  return {
    nodeStatuses: statuses.data ?? [],
    isLoadingStatuses: statuses.isLoading,
    reloadStatuses,
    setupStates,
    importStates,
    setupNode,
    setupNodes,
    importCertificate,
    reset,
  };
}
